import { useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { IndianRupee, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Phase 325d — Bulk-set base_fee dialog.
 * Owner picks a batch + amount → preview (dry-run) → apply.
 */
export default function BulkSetFeeDialog({ open, onClose, batches = [], onApplied }) {
  const [batch, setBatch] = useState(batches[0] || '');
  const [amount, setAmount] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setBatch(batches[0] || '');
    setAmount('');
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!batch || !amount || parseFloat(amount) <= 0) {
      toast.error('Pick a batch and enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/students/bulk-set-fee', {
        batch, amount: parseFloat(amount), dry_run: true,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const res = await api.post('/students/bulk-set-fee', {
        batch, amount: parseFloat(amount), dry_run: false,
      });
      toast.success(`Updated base_fee for ${res.data.updated} students in ${batch}`);
      onApplied?.(res.data);
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose?.(); } }}>
      <DialogContent className="max-w-md" data-testid="bulk-set-fee-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-emerald-600" />
            Bulk-Set Fee by Batch
          </DialogTitle>
          <DialogDescription className="text-xs">
            Apply the same monthly base fee to every active student in a batch.
            Preview first — nothing is saved until you click Apply.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Batch</Label>
            <Select value={batch} onValueChange={(v) => { setBatch(v); setPreview(null); }}>
              <SelectTrigger data-testid="bulk-set-fee-batch">
                <SelectValue placeholder="Select batch" />
              </SelectTrigger>
              <SelectContent>
                {(batches.length ? batches : ['Morning', 'Evening']).map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Monthly fee (₹)</Label>
            <Input
              type="number"
              min="1"
              step="100"
              placeholder="e.g. 2500"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setPreview(null); }}
              data-testid="bulk-set-fee-amount"
            />
          </div>

          {!preview ? (
            <Button
              onClick={handlePreview}
              disabled={loading || !batch || !amount}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-set-fee-preview-btn"
            >
              {loading ? 'Calculating…' : 'Preview matches'}
            </Button>
          ) : (
            <div className="space-y-3" data-testid="bulk-set-fee-preview-result">
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4" /> {preview.matched} students will be updated
                  </p>
                  <p className="font-mono text-xs text-emerald-700">₹{parseFloat(amount).toLocaleString('en-IN')}</p>
                </div>
                {preview.sample?.length > 0 && (
                  <ul className="text-[11px] text-emerald-900/80 space-y-0.5 mt-2 max-h-32 overflow-y-auto">
                    {preview.sample.map((s) => (
                      <li key={s.id} className="flex justify-between">
                        <span className="truncate">{s.name}</span>
                        <span className="font-mono text-emerald-700/70">{s.custom_id}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {preview.matched === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5" /> No active students match this batch.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPreview(null)}
                    disabled={loading}
                    className="text-xs"
                    data-testid="bulk-set-fee-back"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                    data-testid="bulk-set-fee-apply"
                  >
                    {loading ? 'Applying…' : `Apply to ${preview.matched}`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
